"use client"

import { RadialMenu, type RadialMenuOption } from "@/components/ui/radial-menu"
import { playDroppodMenuClickSound } from "@/hooks/use-sound-effects"
import { getHeroismCost, getShrineSkillUpgradeCost } from "@/systems/shrine-system"
import type { HeroSkill } from "@/types/hero.types"

interface RadialShrineMenuProps {
  position: { x: number; y: number }
  money: number
  /** The hero's skills - the shrine offers its three active abilities (hotkeys 1-3) */
  heroSkills: HeroSkill[]
  heroismLevel: number // 0-7
  shrineLevel: number // 1-7 (for future use)
  onUpgradeHeroism: () => void
  onUpgradeHeroSkill: (skillId: string) => void
  onClose: () => void
  isMobile?: boolean
}

// Spritesheet positions (64x64 icons): heroism, skill 1, skill 2, skill 3
const ICON_SIZE = 64
const SPRITE_URL = "/images/shrine/icons_shrine.png"

// Sprite icon component using background position
const SpriteIcon = ({ index }: { index: number }) => (
  <div
    style={{
      width: ICON_SIZE,
      height: ICON_SIZE,
      backgroundImage: `url(${SPRITE_URL})`,
      backgroundPosition: `-${index * ICON_SIZE}px 0`,
      backgroundSize: `${ICON_SIZE * 4}px ${ICON_SIZE}px`,
      imageRendering: "pixelated",
    }}
  />
)

// Skill artwork, rendered at the same size as the shrine sheet icons
const SkillIcon = ({ src, name }: { src: string; name: string }) => (
  <img
    src={src}
    alt={name}
    width={ICON_SIZE}
    height={ICON_SIZE}
    style={{ width: ICON_SIZE, height: ICON_SIZE, imageRendering: "pixelated" }}
    draggable={false}
  />
)

// The shrine's three skill slots are the hero's active abilities, in hotkey
// order. Reading them off the hero keeps this in sync with hero-types.ts - the
// old hardcoded list had drifted and referenced siegesmith skills that no
// longer exist ("siege-barrage", "spore-cloud"), so those slots could never
// have resolved to a real skill.
const getShrineSkills = (heroSkills: HeroSkill[]): Array<HeroSkill | null> => {
  return ["1", "2", "3"].map((hotkey) => heroSkills.find((s) => s.hotkey === hotkey) ?? null)
}

export const RadialShrineMenu = ({
  position,
  money,
  heroSkills,
  heroismLevel,
  onUpgradeHeroism,
  onUpgradeHeroSkill,
  onClose,
  isMobile = false,
}: RadialShrineMenuProps) => {
  const heroismCost = getHeroismCost(heroismLevel)
  const canAffordHeroism = money >= heroismCost && heroismLevel < 7
  const shrineSkills = getShrineSkills(heroSkills)

  const buildSkillOption = (skill: HeroSkill | null, iconIndex: number): RadialMenuOption => {
    if (!skill) {
      return {
        id: `shrine-skill-${iconIndex}`,
        icon: <SpriteIcon index={iconIndex} />,
        disabled: true,
        tooltip: {
          title: "Unavailable",
          description: "This hero has no ability in this slot.",
          titleColor: "#a855f7",
        },
      }
    }

    // The skill's own art, so each slot reads as the ability it buys. The
    // shrine icon sheet only has real artwork for the first two slots - the
    // other two are blank colour swatches.
    const icon = skill.icon ? <SkillIcon src={skill.icon} name={skill.name} /> : <SpriteIcon index={iconIndex} />

    const isMaxed = skill.currentLevel >= skill.maxLevel
    const cost = getShrineSkillUpgradeCost(skill.currentLevel)
    const canAfford = !isMaxed && money >= cost

    return {
      id: skill.id,
      icon,
      label: isMaxed ? undefined : String(cost),
      sublabel: isMaxed ? (
        <span className="text-muted-foreground text-xs font-bold">MAX</span>
      ) : skill.currentLevel > 0 ? (
        <span className="text-amber-400 text-xs font-bold">Lv.{skill.currentLevel}</span>
      ) : (
        <span className="text-sky-300 text-xs font-bold">NEW</span>
      ),
      disabled: !canAfford,
      onClick: () => {
        if (!canAfford) return
        playDroppodMenuClickSound()
        onUpgradeHeroSkill(skill.id)
      },
      tooltip: {
        title: isMaxed
          ? `${skill.name} (Max)`
          : skill.currentLevel === 0
            ? `Unlock ${skill.name}`
            : `${skill.name} Level ${skill.currentLevel + 1}`,
        description: isMaxed ? (
          "MAX LEVEL REACHED"
        ) : (
          <div className="space-y-1">
            <div className="text-xs">{skill.description}</div>
            <div className="text-green-400 text-xs">
              {skill.currentLevel === 0
                ? "Unlocks this ability without spending a skill point"
                : `Level ${skill.currentLevel} → ${skill.currentLevel + 1} for ${cost} gold`}
            </div>
          </div>
        ),
        titleColor: "#a855f7", // Purple
      },
    }
  }

  // Order: heroism (0), skill 1 (1), skill 2 (2), skill 3 (3)
  const options: RadialMenuOption[] = [
    // Heroism option
    {
      id: "heroism",
      icon: <SpriteIcon index={0} />,
      label: heroismLevel >= 7 ? undefined : String(heroismCost),
      sublabel: heroismLevel >= 7 ? (
        <span className="text-muted-foreground text-xs font-bold">MAX</span>
      ) : heroismLevel > 0 ? (
        <span className="text-amber-400 text-xs font-bold">Lv.{heroismLevel}</span>
      ) : undefined,
      disabled: !canAffordHeroism,
      onClick: () => {
        if (canAffordHeroism) {
          playDroppodMenuClickSound()
          onUpgradeHeroism()
        }
      },
      tooltip: {
        title: heroismLevel === 0 ? "Heroism" : `Heroism Level ${heroismLevel + 1}`,
        description:
          heroismLevel >= 7
            ? "MAX LEVEL REACHED"
            : (
                <div className="space-y-1">
                  <div className="text-yellow-200 font-semibold text-xs">Heroes in shrine aura:</div>
                  <div className="text-green-400 text-xs">
                    +7.5% speed, +5% XP, +5% HP, +3% damage, +1 armor
                  </div>
                  <div className="text-red-400 text-xs">
                    Tower cost +2%
                  </div>
                </div>
              ),
        titleColor: "#fbbf24", // Amber/gold
      },
    },
    // The hero's three active abilities
    buildSkillOption(shrineSkills[0], 1),
    buildSkillOption(shrineSkills[1], 2),
    buildSkillOption(shrineSkills[2], 3),
  ]

  return (
    <RadialMenu
      position={position}
      options={options}
      radius={70}
      iconSize={72}
      onClose={onClose}
      isMobile={isMobile}
      itemClassName="!rounded-none !border-0"
      noBorder={true}
    />
  )
}
